import React, { useState, useEffect } from 'react';
import { Container } from "./styles";
import { FiDownload, FiPlay, FiSearch, FiPlus, FiTrash2, FiCalendar } from "react-icons/fi";
import { GoFile, GoCheck, GoAlert, GoX, GoSync, GoOrganization } from "react-icons/go";
import { BiSpreadsheet } from "react-icons/bi";
import api from '../../services/api';
import { toast } from 'react-toastify';
import NovoDownloadModal from './components/NovoDownloadModal';
import ProcessamentoModal from './components/ProcessamentoModal';

export default function SpedDownload({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [downloads, setDownloads] = useState([]);
  const [downloadSearch, setDownloadSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showProcessamentoModal, setShowProcessamentoModal] = useState(false);
  const [currentOutputId, setCurrentOutputId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [availableStats, setAvailableStats] = useState({
    totalDocuments: 0,
    periodMin: null,
    periodMax: null,
    totalCompanies: 0,
    totalSize: '0 MB'
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [downloadingZip, setDownloadingZip] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});

  useEffect(() => {
    fetchDownloads();
    fetchAvailableStats();
  }, []);

  useEffect(() => {
    console.log('[Estado] downloadingZip:', downloadingZip);
    console.log('[Estado] downloadProgress:', downloadProgress);
  }, [downloadingZip, downloadProgress]);

  const fetchAvailableStats = async () => {
    setLoadingStats(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      if (!projectId) {
        setLoadingStats(false);
        return;
      }
      
      const response = await api.get(`/sped-download/stats/${projectId}`);
      
      if (response.data.success && response.data.stats) {
        setAvailableStats(response.data.stats);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas disponíveis:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sped-download/outputs');
      
      if (response.data.outputs) {
        const formattedDownloads = response.data.outputs.map(output => {
          const periodo = output.periodo 
            ? `${new Date(output.periodo.inicio).toLocaleDateString('pt-BR')} - ${new Date(output.periodo.fim).toLocaleDateString('pt-BR')}`
            : 'Download';
          
          return {
            id: output.outputId,
            periodo,
            date: output.geradoEm,
            totalSpeds: output.totalSpeds || 0,
            totalEmpresas: output.totalEmpresas || 0,
            tamanhoZipMB: output.tamanhoZipMB || 0,
            hasZip: output.hasZip
          };
        });
        
        formattedDownloads.sort((a, b) => new Date(b.date) - new Date(a.date));
        setDownloads(formattedDownloads);
      }
    } catch (error) {
      console.error('Erro ao carregar downloads:', error);
      toast.error('Erro ao carregar histórico de downloads');
    } finally {
      setLoading(false);
    }
  };

  const totalStats = downloads.reduce((acc, download) => ({
    totalDownloads: acc.totalDownloads + 1,
    totalSpeds: acc.totalSpeds + (download.totalSpeds || 0),
    totalEmpresas: acc.totalEmpresas + (download.totalEmpresas || 0),
    tamanhoZipMB: acc.tamanhoZipMB + (download.tamanhoZipMB || 0)
  }), { totalDownloads: 0, totalSpeds: 0, totalEmpresas: 0, tamanhoZipMB: 0 });

  const lastDownload = downloads.length > 0 
    ? new Date(downloads[0].date).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  const handleStartDownload = () => {
    setShowDownloadModal(true);
  };

  const handleProcessDownload = async (downloadData) => {
    setShowDownloadModal(false);
    
    try {
      console.log('[SpedDownload] Enviando dados:', downloadData);
      
      // Enviar para backend
      const response = await api.post('/sped-download/process', downloadData);
      
      console.log('[SpedDownload] Resposta do backend:', response.data);
      
      if (response.data.success && response.data.outputId) {
        const { outputId } = response.data;
        
        console.log('[SpedDownload] OutputId recebido:', outputId);
        
        // Armazenar outputId e abrir modal de processamento
        setCurrentOutputId(outputId);
        setShowProcessamentoModal(true);
        
        toast.success('Processamento iniciado!');
      } else {
        console.error('[SpedDownload] Resposta sem outputId:', response.data);
        toast.error('Erro: outputId não retornado pelo servidor');
      }
    } catch (error) {
      console.error('Erro ao iniciar download:', error);
      toast.error(error.response?.data?.message || 'Erro ao iniciar download');
    }
  };

  const handleDeleteDownload = (outputId, downloadName) => {
    setDeleteConfirm({ outputId, downloadName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const { outputId } = deleteConfirm;
    
    try {
      setDownloads(prev => prev.filter(d => d.id !== outputId));
      setDeleteConfirm(null);
      
      await api.delete(`/sped-download/output/${outputId}`);
      toast.success('Download deletado com sucesso');
    } catch (error) {
      console.error('Erro ao deletar download:', error);
      toast.error('Erro ao deletar download');
      fetchDownloads();
    }
  };

  const handleDownloadZip = async (outputId) => {
    console.log('🚀 [handleDownloadZip] INICIANDO - outputId:', outputId);
    try {
      console.log('📝 [handleDownloadZip] Setando downloadingZip para:', outputId);
      setDownloadingZip(outputId);
      
      console.log('📝 [handleDownloadZip] Setando downloadProgress inicial para 0');
      setDownloadProgress(prev => ({ ...prev, [outputId]: 0 }));
      
      console.log('🌐 [handleDownloadZip] Iniciando requisição API...');
      const response = await api.get(`/sped-download/download-zip?outputId=${outputId}`, {
        responseType: 'blob',
        timeout: 600000,
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
          console.log('📊 [handleDownloadZip] Progresso:', percentCompleted, '%');
          setDownloadProgress(prev => ({ ...prev, [outputId]: percentCompleted }));
        }
      });
      
      console.log('✅ [handleDownloadZip] Resposta recebida, tamanho:', response.data.size);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `speds_${outputId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download concluído!');
      
      setTimeout(() => {
        setDownloadingZip(null);
        setDownloadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[outputId];
          return newProgress;
        });
      }, 1000);
    } catch (error) {
      console.error('Erro ao baixar ZIP:', error);
      toast.error('Erro ao baixar arquivo ZIP');
      setDownloadingZip(null);
      setDownloadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[outputId];
        return newProgress;
      });
    }
  };

  return (
    <Container>
      {!embedded && (
        <>
          {/* Header */}
          <div className="header-info">
            <div className="header-icon">
              <FiDownload />
            </div>
            <div className="header-details">
              <h3>Download de SPEDs</h3>
              <div className="header-meta">
                <span className="status-tag active">Sistema Ativo</span>
                <span className="update-tag">
                  {downloads.length > 0 ? `Último download: ${lastDownload}` : 'Aguardando primeiro download'}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs - Iniciar Download */}
          <div className="tabs-header">
            <button 
              className="tab active"
              onClick={handleStartDownload}
              disabled={downloading}
            >
              {downloading ? <GoSync className="spin" /> : <FiPlay />}
              {downloading ? 'Baixando SPEDs...' : 'Iniciar Download'}
            </button>
          </div>
        </>
      )}

      {/* KPI Cards */}
      {loadingStats ? (
        <div className="kpi-cards main-stats">
          <div className="kpi-card primary">
            <div className="card-content">
              <div className="thumb"><GoSync className="spin" /></div>
              <div className="text">
                <span className="kpi-label">Carregando...</span>
                <span className="kpi-value">-</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="kpi-cards main-stats">
          <div className="kpi-card primary">
            <div className="card-content">
              <div className="thumb"><GoFile /></div>
              <div className="text">
                <span className="kpi-label">Total de Documentos</span>
                <span className="kpi-value">{availableStats.totalDocuments.toLocaleString('pt-BR')}</span>
              </div>
            </div>
          </div>

          <div className="kpi-card success">
            <div className="card-content">
              <div className="thumb"><FiCalendar /></div>
              <div className="text">
                <span className="kpi-label">Período</span>
                <span className="kpi-value">{availableStats.periodMin && availableStats.periodMax ? `${availableStats.periodMin} - ${availableStats.periodMax}` : '-'}</span>
              </div>
            </div>
          </div>

          <div className="kpi-card warning">
            <div className="card-content">
              <div className="thumb"><GoOrganization /></div>
              <div className="text">
                <span className="kpi-label">Empresas</span>
                <span className="kpi-value">{availableStats.totalCompanies}</span>
              </div>
            </div>
          </div>

          <div className="kpi-card info">
            <div className="card-content">
              <div className="thumb"><FiDownload /></div>
              <div className="text">
                <span className="kpi-label">Tamanho Total</span>
                <span className="kpi-value">{availableStats.totalSize}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar - Downloads Realizados e Busca */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="toolbar-btn">
            <BiSpreadsheet />
            Downloads Realizados
          </button>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar download por ID ou período..."
              value={downloadSearch}
              onChange={(e) => setDownloadSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Lista de Downloads */}
      <div className="downloads-section">
        <div className="downloads-grid">
          {/* Card Novo Download */}
          <div className="download-card new-download" onClick={handleStartDownload}>
            <div className="new-download-content">
              <div className="new-download-icon">
                <FiPlus />
              </div>
              <div className="new-download-text">
                <h5>Novo Download</h5>
                <p>Baixar SPEDs por período e empresas</p>
              </div>
            </div>
          </div>

          {/* Downloads Existentes */}
          {downloads
            .filter(download => 
              download.id.toLowerCase().includes(downloadSearch.toLowerCase()) ||
              (download.periodo && download.periodo.toLowerCase().includes(downloadSearch.toLowerCase()))
            )
            .map(download => (
              <div 
                key={download.id} 
                className="download-card"
                onClick={() => {
                  console.log('🖱️ [Card Click] Download:', {
                    id: download.id,
                    hasZip: download.hasZip,
                    totalSpeds: download.totalSpeds,
                    tamanhoZipMB: download.tamanhoZipMB
                  });
                  if (download.hasZip) {
                    console.log('✅ [Card Click] hasZip é TRUE, chamando handleDownloadZip...');
                    handleDownloadZip(download.id);
                  } else {
                    console.log('❌ [Card Click] hasZip é FALSE, não fazendo nada');
                  }
                }}
                style={{ cursor: download.hasZip ? 'pointer' : 'default' }}
                title={download.hasZip ? 'Clique para baixar ZIP' : 'ZIP não disponível'}
              >
                <span className="file-size-left">{download.tamanhoZipMB.toFixed(1)} MB</span>
                <button 
                  className="delete-btn-corner"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteDownload(download.id, download.periodo || download.id);
                  }}
                  title="Deletar download"
                >
                  <FiTrash2 />
                </button>
                
                <div className="download-header">
                  <div className={`download-icon ${downloadingZip === download.id ? 'downloading' : ''}`}>
                    {downloadingZip === download.id ? <GoSync className="spin" /> : <FiDownload />}
                  </div>
                  <div className="download-info">
                    <h5>{download.id}</h5>
                    <span className="periodo">{download.periodo}</span>
                    <span className="date">{new Date(download.date).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
                
                {downloadingZip === download.id && downloadProgress[download.id] !== undefined && (
                  <div className="download-progress-bar">
                    <div 
                      className="download-progress-fill" 
                      style={{ width: `${downloadProgress[download.id]}%` }}
                    />
                    <span className="download-progress-text">{downloadProgress[download.id]}%</span>
                  </div>
                )}
                
                <div className="download-meta">
                  <div className="meta-item">
                    <GoFile />
                    <span className="meta-value">{download.totalSpeds} SPEDs</span>
                  </div>
                  <div className="meta-item success">
                    <GoOrganization />
                    <span className="meta-value">{download.totalEmpresas} empresas</span>
                  </div>
                </div>

                <div className="download-footer">
                  <span><FiCalendar /> {download.periodo}</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Modal de Novo Download */}
      <NovoDownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onStartDownload={handleProcessDownload}
      />

      {/* Modal de Processamento */}
      <ProcessamentoModal
        isOpen={showProcessamentoModal}
        onClose={() => setShowProcessamentoModal(false)}
        onComplete={fetchDownloads}
        outputId={currentOutputId}
      />

      {/* Modal de Confirmação de Deleção */}
      {deleteConfirm && (
        <div className="delete-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <FiTrash2 />
            </div>
            <div className="delete-modal-content">
              <h4>Excluir download</h4>
              <p>Tem certeza que deseja excluir o download</p>
              <span className="delete-modal-filename">
                {deleteConfirm.downloadName.length > 40
                  ? deleteConfirm.downloadName.substring(0, 40) + '...'
                  : deleteConfirm.downloadName
                }
              </span>
              <p className="delete-modal-warning">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="delete-modal-actions">
              <button className="delete-modal-cancel" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </button>
              <button className="delete-modal-confirm" onClick={confirmDelete}>
                <FiTrash2 />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
